import { extractStatsFromRow, PlayerStats } from './xovr-engine';
import { gcInningsToDecimal } from './gc-innings';

export interface ParsedGCPlayer {
  last: string;
  first: string;
  number: string;
  stats: PlayerStats | null;
  fieldTime: Record<string, number> | null;  // innings at each position: { P: 0, C: 2, SS: 3, ... }
}

export interface ParsedFilename {
  teamName: string;
  season: string;
  seasonYear: number;
}

// Parses GC export filename: "[Team Name] [Season] [Year] Stats*.csv"
export function parseGCFilename(filename: string): ParsedFilename | null {
  const bare = filename.replace(/\.csv$/i, '').replace(/\s*Stats.*$/i, '').trim();
  for (const season of ['Spring', 'Summer', 'Fall', 'Winter']) {
    const match = bare.match(new RegExp(`^(.+?)\\s+${season}\\s+(\\d{4})`, 'i'));
    if (match) {
      return { teamName: match[1].trim(), season, seasonYear: parseInt(match[2], 10) };
    }
  }
  return null;
}

// Parses a GameChanger season stats CSV (raw Papa.parse output, header: false).
// Returns one entry per player row, skipping totals/glossary rows.
export function parseGCCsv(raw: any[][]): ParsedGCPlayer[] {
  const headerIdx = raw.findIndex(row =>
    row.some(cell => String(cell).trim() === 'Number') &&
    row.some(cell => String(cell).trim() === 'Last')
  );
  if (headerIdx === -1) return [];

  const headers: string[] = raw[headerIdx].map(h => String(h ?? '').trim());

  // GC CSVs have repeated column names across Batting / Pitching / Fielding sections.
  // IP marks the start of pitching; TC marks the start of fielding.
  const ipIdx = headers.indexOf('IP');
  const tcIdx = headers.indexOf('TC');

  const battingMap: Record<string, number> = {};
  const pitchingMap: Record<string, number> = {};
  // Fielding stats (TC, A, PO, FPCT, E, DP, TP, INN, PB, SB, SBATT, CS, CS%,
  // PIK, CI) plus the trailing Total. Deliberately separate from positionIdxMap
  // below, which covers only the per-position innings columns after CI. Column
  // names repeat across all three sections (SB, CS, PIK, CI, H), so each map
  // must be built strictly within its own range or the wrong column wins.
  const fieldingMap: Record<string, number> = {};

  headers.forEach((h, i) => {
    if (ipIdx !== -1 && tcIdx !== -1) {
      if (i < ipIdx) {
        if (!(h in battingMap)) battingMap[h] = i;
      } else if (i < tcIdx) {
        if (!(h in pitchingMap)) pitchingMap[h] = i;
      } else {
        if (!(h in fieldingMap)) fieldingMap[h] = i;
      }
    } else {
      if (!(h in battingMap)) battingMap[h] = i;
    }
  });

  // Position innings columns come after CI (catcher's interference) in the fielding section.
  // Columns: P, C, 1B, 2B, 3B, SS, LF, CF, RF, SF, Total
  const POSITION_COLS = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'SF'];
  const positionIdxMap: Record<string, number> = {};
  if (tcIdx !== -1) {
    // Find CI column index anywhere after tcIdx
    let ciIdx = -1;
    for (let j = tcIdx + 1; j < headers.length; j++) {
      if (headers[j] === 'CI') { ciIdx = j; break; }
    }
    if (ciIdx !== -1) {
      POSITION_COLS.forEach(pos => {
        for (let j = ciIdx + 1; j < headers.length; j++) {
          if (headers[j] === pos) { positionIdxMap[pos] = j; break; }
        }
      });
    }
  }

  const results: ParsedGCPlayer[] = [];

  for (let i = headerIdx + 1; i < raw.length; i++) {
    const row = raw[i];
    if (!row || row.every((c: any) => c === '' || c == null)) continue;

    const last  = String(row[battingMap['Last']   ?? 1] ?? '').trim();
    const first = String(row[battingMap['First']  ?? 2] ?? '').trim();
    const num   = String(row[battingMap['Number'] ?? 0] ?? '').trim();

    if (!last || last === 'Totals' || last === 'Glossary' || last.startsWith('GP=')) continue;

    const stats = extractStatsFromRow(row, battingMap, pitchingMap, fieldingMap);

    let fieldTime: Record<string, number> | null = null;
    if (Object.keys(positionIdxMap).length > 0) {
      const ft: Record<string, number> = {};
      POSITION_COLS.forEach(pos => {
        const idx = positionIdxMap[pos];
        if (idx !== undefined) {
          // GC writes fielding innings as OUTS: "3.2" is three and TWO THIRDS,
          // not three-point-two. Convert here, at the edge, so everything stored
          // downstream is a true decimal inning.
          //
          // parseFloat used to store GC's notation verbatim, which only ever
          // worked because the read side re-parsed the number back into a
          // string and re-interpreted the tenths digit as thirds. That round
          // trip is fragile (it depends on JS reprinting 3.2 as exactly "3.2")
          // and it silently understates every total that is summed before it
          // reaches that read path, which is why Field Time Equalizer bench
          // math and the per-player benchLog were short.
          const val = gcInningsToDecimal(row[idx]);
          if (val > 0) ft[pos] = val;
        }
      });
      if (Object.keys(ft).length > 0) fieldTime = ft;
    }

    results.push({ last, first, number: num, stats, fieldTime });
  }

  return results;
}
